import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LOG_CONTEXT } from '@rms/core/http';
import { pagedParams } from '@rms/core/pagination';
import { environment } from '@rms/env';
import { PageableParam, Paginated } from '@rms/sdk/models';
import { Filter } from '@rms/ui/filters';
import { Observable } from 'rxjs';

import {
  Partner,
  PartnerCreateRequest,
  PartnerPatchRequest,
  PartnerRole,
  PartnerUserCreateRequest,
  PartnerUserResponse,
  PartnerUsersScope,
  PartnerUserUpdateRequest,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class PartnerApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiEndpoint}/v1/im/partners`;
  private readonly ownPartnerUrl = `${environment.apiEndpoint}/v1/partners`;

  public getPartners(pageable: PageableParam, filters: Filter[] = []): Observable<Paginated<Partner>> {
    return this.http.get<Paginated<Partner>>(this.baseUrl, {
      params: pagedParams(pageable, filters),
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartners'),
    });
  }

  public getPartner(partnerId: string): Observable<Partner> {
    return this.http.get<Partner>(`${this.baseUrl}/${partnerId}`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartner'),
    });
  }

  public getPartnerUsers(
    scope: PartnerUsersScope,
    pageable: PageableParam,
    filters: Filter[] = []
  ): Observable<Paginated<PartnerUserResponse>> {
    return this.http.get<Paginated<PartnerUserResponse>>(this.usersUrl(scope), {
      params: pagedParams(pageable, filters),
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartnerUsers'),
    });
  }

  public createPartnerUser(
    scope: PartnerUsersScope,
    request: PartnerUserCreateRequest
  ): Observable<PartnerUserResponse> {
    return this.http.post<PartnerUserResponse>(this.usersUrl(scope), request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.createPartnerUser'),
    });
  }

  public updatePartnerUser(
    scope: PartnerUsersScope,
    userId: string,
    request: PartnerUserUpdateRequest
  ): Observable<PartnerUserResponse> {
    return this.http.put<PartnerUserResponse>(`${this.usersUrl(scope)}/${userId}`, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.updatePartnerUser'),
    });
  }

  public getPartnerRoles(scope: PartnerUsersScope): Observable<PartnerRole[]> {
    return this.http.get<PartnerRole[]>(`${'partnerId' in scope ? this.baseUrl : this.ownPartnerUrl}/roles`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.getPartnerRoles'),
    });
  }

  public updatePartnerUserRole(
    scope: PartnerUsersScope,
    userId: string,
    roleIds: string[]
  ): Observable<PartnerUserResponse> {
    return this.http.put<PartnerUserResponse>(
      `${this.usersUrl(scope)}/${userId}/roles`,
      { role_ids: roleIds },
      { context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.updatePartnerUserRole') }
    );
  }

  public setPartnerUserPassword(partnerId: string, userId: string, password: string): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/${partnerId}/users/${userId}/credentials`,
      { password },
      { context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.setPartnerUserPassword') }
    );
  }

  public deletePartnerUser(scope: PartnerUsersScope, userId: string): Observable<void> {
    return this.http.delete<void>(`${this.usersUrl(scope)}/${userId}`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.deletePartnerUser'),
    });
  }

  public createPartner(request: PartnerCreateRequest): Observable<Partner> {
    return this.http.post<Partner>(this.baseUrl, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.createPartner'),
    });
  }

  public updatePartner(partnerId: string, request: PartnerPatchRequest): Observable<Partner> {
    return this.http.patch<Partner>(`${this.baseUrl}/${partnerId}`, request, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.updatePartner'),
    });
  }

  public deletePartner(partnerId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${partnerId}`, {
      context: new HttpContext().set(LOG_CONTEXT, 'PartnerApiService.deletePartner'),
    });
  }

  private usersUrl(scope: PartnerUsersScope): string {
    return 'partnerId' in scope ? `${this.baseUrl}/${scope.partnerId}/users` : `${this.ownPartnerUrl}/users`;
  }
}
